import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import {
  canConfigureDevices,
  canSeeAdmin,
  canSeeAdminShell,
} from './adminAccess'
import type { MeResponse } from '../api/auth'

/**
 * Role gates for the admin surfaces. All share the same
 * hydrating/anonymous handling; they differ only in the capability check:
 *
 *  - `RequireAdminShell` — can enter /admin at all (business OR systems admin).
 *  - `RequireSuperadmin` — full business admin (users, sessions, cuestionarios…).
 *  - `RequireDeviceAdmin` — can configure devices (`device_admin` or a
 *    full administrator).
 * Authentication and permissions are required in every environment.
 */
function RoleGate({
  children,
  can,
  denied,
}: {
  children: ReactNode
  can: (user: MeResponse | null | undefined) => boolean
  denied: string
}) {
  const { status, user } = useAuth()
  const location = useLocation()

  // Mismo criterio que `RequireAuth`: una re-hidratacion con `user` ya
  // conocido no tira abajo el arbol de /admin, solo la hidratacion inicial.
  if ((status === 'idle' || status === 'hydrating') && !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-text-muted">
        Cargando…
      </div>
    )
  }
  if (status === 'anonymous') {
    const next = `${location.pathname}${location.search}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }
  if (!can(user)) {
    return (
      <div className="mx-auto max-w-md px-lg py-2xl text-center">
        <h1>403</h1>
        <p className="text-text-muted">{denied}</p>
      </div>
    )
  }
  return <>{children}</>
}

export function RequireAdminShell({ children }: { children: ReactNode }) {
  return (
    <RoleGate can={canSeeAdminShell} denied="No tenés acceso al panel de admin.">
      {children}
    </RoleGate>
  )
}

export function RequireSuperadmin({ children }: { children: ReactNode }) {
  return (
    <RoleGate can={canSeeAdmin} denied="No tenés permisos de administrador.">
      {children}
    </RoleGate>
  )
}

export function RequireDeviceAdmin({ children }: { children: ReactNode }) {
  return (
    <RoleGate
      can={canConfigureDevices}
      denied="No tenés permisos para administrar dispositivos."
    >
      {children}
    </RoleGate>
  )
}
